import React, { useEffect, useState } from "react";
import Image from "next/image";
import useMedia from "react-use/lib/useMedia";
import ReactCountryFlag from "react-country-flag";
import { Link as NextLink } from "../Link";
import { CountryChangeModal } from "../CountryChangeModal";
import { Wrapper } from "../Wrapper";
import { TrustPilotTrustBox } from "../TrustPilotTrustBox";
import {
  FooterBox,
  FooterDivider,
  FWrapper,
  LogoLinkWrapper,
  FooterGrid,
  FooterBoxWrapper,
  SectionBelowDivider,
  SectionAboveDivider,
  WrapperInner,
  FooterItem,
  FooterLink,
  StyledFooterLink,
  PolicyWrapper,
  PolicySectionWrapper,
  ChangeLocation,
  ChangeLocationLink,
  LogosWrapper,
  FInnerWrapper,
  FooterBoxHeader,
  CopyRightHeaderWrapper,
  CopyRightHeader,
  LogoImage,
  BCorpLogo,
  FooterItemTrustPilot,
  BCorpLogosWrapper,
  EcoSystemLogoWrapper,
  BCorpLogoWrapper,
  StyledFooterSigninLink,
} from "./Footer.styles";
import { desktopUp } from "../theme/ecologi-breakpoints";
import {
  ECOLOGI_INSTAGRAM_URL,
  ECOLOGI_LINKEDIN_URL,
  ECOLOGI_X_URL,
  ECOLOGI_YOUTUBE_URL,
} from "@ecologi/shared";

export type FooterProps = {
  linkComponent?: any;
  location: {
    countryCode: string;
  } | null;
  user?: {
    username: string;
  } | null;
  setUserLocation: (update: { countryCode: string }) => void;
};

export const Footer = ({
  user,
  linkComponent = NextLink,
  location,
  setUserLocation,
}: FooterProps) => {
  // TODO: Typedefinitions for link component
  const Link = linkComponent;
  const isMobile = useMedia("(max-width: 560px)", false);

  const isDesktop = useMedia(desktopUp, true);

  const [isCountryPopupVisible, setCountryPopupState] = useState(false);

  const [useFallbackTrustPilot, setUseFallbackTrustPilot] = useState(true);
  const currentYear = new Date().getFullYear();

  const [showChangeLocation, setShowChangeLocation] = useState(false);

  useEffect(() => {
    setShowChangeLocation(Boolean(location && !user));
  }, [location, user]);

  return (
    <>
      <FWrapper id="__ecologi-footer">
        <FInnerWrapper>
          <SectionAboveDivider>
            <LogoLinkWrapper>
              <Link href="/" title="Go to the home page">
                <LogoImage
                  src="/images/app/footer/logo_white.svg"
                  alt="Ecologi"
                  loading="lazy"
                />
              </Link>
            </LogoLinkWrapper>
            <Wrapper isLarge>
              <WrapperInner>
                <FooterGrid>
                  <FooterBoxWrapper>
                    <FooterBox>
                      <li>
                        <FooterBoxHeader>Follow us</FooterBoxHeader>
                      </li>
                      <FooterItem>
                        <FooterLink
                          href={ECOLOGI_LINKEDIN_URL}
                          rel="noopener noreferrer"
                          target="_blank"
                          title="Follow us on LinkedIn (new tab)"
                        >
                          LinkedIn
                        </FooterLink>
                      </FooterItem>
                      <FooterItem>
                        <FooterLink
                          href={ECOLOGI_INSTAGRAM_URL}
                          rel="noopener noreferrer"
                          target="_blank"
                          title="Follow us on Instagram (new tab)"
                        >
                          Instagram
                        </FooterLink>
                      </FooterItem>
                      <FooterItem>
                        <FooterLink
                          href={ECOLOGI_X_URL}
                          rel="noopener noreferrer"
                          target="_blank"
                          title="Follow us on X (new tab)"
                        >
                          X
                        </FooterLink>
                      </FooterItem>
                      <FooterItem>
                        <FooterLink
                          href={ECOLOGI_YOUTUBE_URL}
                          rel="noopener noreferrer"
                          target="_blank"
                          title="Follow us on YouTube (new tab)"
                        >
                          YouTube
                        </FooterLink>
                      </FooterItem>
                    </FooterBox>
                  </FooterBoxWrapper>
                  <FooterBoxWrapper>
                    <FooterBox>
                      <li>
                        <FooterBoxHeader>Members</FooterBoxHeader>
                      </li>
                      {/* Login link needs to be its own component so we can use the media query hook */}
                      <FooterItem>
                        <StyledFooterSigninLink
                          href={user ? "/auth/logout" : "/auth/login"}
                        >
                          {user ? "Sign out" : "Sign in"}
                        </StyledFooterSigninLink>
                      </FooterItem>
                      <FooterItem>
                        <StyledFooterLink href="/pay-as-you-go">
                          Create an account
                        </StyledFooterLink>
                      </FooterItem>
                      <FooterItem>
                        <StyledFooterLink href="/solutions/partnerships">
                          Partnerships
                        </StyledFooterLink>
                      </FooterItem>
                    </FooterBox>
                  </FooterBoxWrapper>
                  <FooterBoxWrapper>
                    <FooterBox>
                      <li>
                        <FooterBoxHeader>Resources</FooterBoxHeader>
                      </li>
                      <FooterItem>
                        <FooterLink
                          href="https://docs.ecologi.com"
                          rel="noopener noreferrer"
                          target="_blank"
                          title="View our API docs (new tab)"
                        >
                          API docs
                        </FooterLink>
                      </FooterItem>
                      <FooterItem>
                        <StyledFooterLink href="/resources/press">
                          Press
                        </StyledFooterLink>
                      </FooterItem>
                      <FooterItem>
                        <FooterLink
                          href="https://careers.ecologi.com"
                          rel="noopener noreferrer"
                          target="_blank"
                          title="View our openings (new tab)"
                        >
                          Join the team
                        </FooterLink>
                      </FooterItem>
                    </FooterBox>
                    {isDesktop && (
                      <FooterBox>
                        <FooterItemTrustPilot>
                          <TrustPilotTrustBox
                            componentStyles={{
                              isWrapperCentralized: false,
                            }}
                            onLoaded={() => setUseFallbackTrustPilot(false)}
                            onErrored={() => setUseFallbackTrustPilot(true)}
                            theme="dark"
                            templateId="53aa8807dec7e10d38f59f32"
                            width="150px"
                            height="94px"
                          />
                          {/* Fallback if Trustpilot failed to load for Adblock/script failure */}
                          {useFallbackTrustPilot && (
                            <a
                              href="https://uk.trustpilot.com/review/ecologi.com"
                              target="_blank"
                              title="View our 5 star reviews on Trustpilot"
                              rel="noopener noreferrer"
                            >
                              <Image
                                src="/images/app/trustpilot-mark.svg"
                                alt="Trustpilot 5 stars"
                                height={32}
                                width={130}
                                unoptimized
                              />
                            </a>
                          )}
                        </FooterItemTrustPilot>
                      </FooterBox>
                    )}
                  </FooterBoxWrapper>

                  {!isDesktop && (
                    <FooterBoxWrapper>
                      <FooterBox>
                        <FooterItem>
                          <TrustPilotTrustBox
                            componentStyles={{
                              isWrapperCentralized: false,
                            }}
                            onLoaded={() => setUseFallbackTrustPilot(false)}
                            onErrored={() => setUseFallbackTrustPilot(true)}
                            theme="dark"
                            templateId="53aa8807dec7e10d38f59f32"
                            width="150px"
                            height="94px"
                          />
                          {/* Fallback if Trustpilot failed to load for Adblock/script failure */}
                          {useFallbackTrustPilot && (
                            <a
                              href="https://uk.trustpilot.com/review/ecologi.com"
                              target="_blank"
                              title="View our 5 star reviews on Trustpilot"
                              rel="noopener noreferrer"
                            >
                              <Image
                                src="/images/app/trustpilot-mark.svg"
                                alt="Trustpilot 5 stars"
                                height={32}
                                width={130}
                                unoptimized
                              />
                            </a>
                          )}
                        </FooterItem>
                      </FooterBox>
                    </FooterBoxWrapper>
                  )}
                </FooterGrid>
              </WrapperInner>
            </Wrapper>
          </SectionAboveDivider>

          <FooterDivider />

          <SectionBelowDivider>
            <div>
              <PolicySectionWrapper>
                <PolicyWrapper>
                  <CopyRightHeaderWrapper
                    showChangeLocation={showChangeLocation}
                  >
                    <CopyRightHeader>
                      &copy; {`${currentYear}`} Ecologi
                    </CopyRightHeader>
                  </CopyRightHeaderWrapper>
                  <FooterItem>
                    <FooterLink
                      aria-label="ecologi privacy policy"
                      href="/resources/privacy-policy"
                      title="View our Privacy Policy"
                    >
                      Privacy Policy
                    </FooterLink>
                  </FooterItem>
                  <FooterItem>
                    <FooterLink
                      aria-label="ecologi cookie policy"
                      href="/resources/cookie-policy"
                      title="View our Cookie Policy"
                    >
                      Cookie Policy
                    </FooterLink>
                  </FooterItem>
                  <FooterItem>
                    <FooterLink
                      aria-label="ecologi terms and conditions"
                      href="/resources/terms-and-conditions"
                      title="View our Terms & Conditions"
                    >
                      Terms & Conditions
                    </FooterLink>
                  </FooterItem>
                </PolicyWrapper>
              </PolicySectionWrapper>

              {showChangeLocation && (
                <ChangeLocation as="div">
                  <ChangeLocationLink
                    href="#switch-region"
                    title="Switch region"
                    data-flag={true}
                    onClick={(e) => {
                      e.preventDefault();
                      setCountryPopupState(true);
                    }}
                  >
                    <ReactCountryFlag
                      countryCode={location?.countryCode || ""}
                      style={{
                        width: "27px",
                        height: "18px",
                        marginRight: "12px",
                        verticalAlign: "inherit",
                      }}
                      svg
                    />
                    Change location
                  </ChangeLocationLink>
                </ChangeLocation>
              )}
            </div>

            {isMobile && (
              <EcoSystemLogoWrapper>
                <a
                  href="https://www.decadeonrestoration.org/partners"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View the UN Decade partners"
                >
                  <img
                    src="/images/app/footer/un_decade_logo.png"
                    alt="United Nations Decade on Ecosystem Restoration 2021-2030"
                    width="176"
                    height="120"
                    loading="lazy"
                  />
                </a>
              </EcoSystemLogoWrapper>
            )}
            <LogosWrapper $isMobile={isMobile}>
              {!isMobile && (
                <a
                  href="https://www.decadeonrestoration.org/partners"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View the UN Decade partners"
                >
                  <img
                    src="/images/app/footer/un_decade_logo.png"
                    alt="United Nations Decade on Ecosystem Restoration 2021-2030"
                    width="176"
                    height="120"
                    loading="lazy"
                  />
                </a>
              )}
              <BCorpLogosWrapper $isMobile={isMobile}>
                <a
                  href="https://www.bcorporation.net/en-us/find-a-b-corp/company/ecologi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View the Ecologi B Corp page"
                >
                  <BCorpLogoWrapper $isCertifiedLogo>
                    <BCorpLogo
                      src="/images/app/footer/b-corp-logo@2x.png"
                      alt="Certified B Corporation"
                      loading="lazy"
                    />
                  </BCorpLogoWrapper>
                </a>
                <a
                  href="https://bcorporation.eu/best-for-the-world/"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View the Best for the World page"
                >
                  <BCorpLogoWrapper>
                    <BCorpLogo
                      src="/images/app/footer/bftw-governance@2x.png"
                      alt="Certified B Corporation"
                      loading="lazy"
                    />
                  </BCorpLogoWrapper>
                </a>
                <a
                  href="https://bcorporation.eu/best-for-the-world/"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View the Best for the World page"
                >
                  <BCorpLogoWrapper>
                    <BCorpLogo
                      src="/images/app/footer/bftw-environment@2x.png"
                      alt="Certified B Corporation"
                      loading="lazy"
                    />
                  </BCorpLogoWrapper>
                </a>
              </BCorpLogosWrapper>
            </LogosWrapper>
          </SectionBelowDivider>
        </FInnerWrapper>
      </FWrapper>
      <CountryChangeModal
        setUserLocation={setUserLocation}
        location={location}
        onClose={() => setCountryPopupState(false)}
        isOpen={isCountryPopupVisible}
      />
    </>
  );
};
